<!--
  Copyright 2023 The Ground Authors.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
-->

<form [formGroup]="formGroup" class="task-input" [ngClass]="{'selected': expanded()}" cdkDrag>
  <mat-icon class="drag-icon" cdkDragHandle>drag_handle</mat-icon>

  <div class="task-toolbar">
    <div
      *ngIf="GeometryTasks.includes(taskGroup)"
      [matTooltip]="typeLocked() ? typeLockedMessage : ''"
    >
      <mat-select
        [(value)]="taskGroup"
        [disabled]="typeLocked()"
        [hideSingleSelectionIndicator]="true"
        (selectionChange)="onTaskGroupSelect($event.value)"
      >
        <mat-select-trigger class="task-label">
          <mat-icon>{{ Tasks[taskGroup].icon }}</mat-icon>
          <div>{{ Tasks[taskGroup].label }}</div>
        </mat-select-trigger>

        <mat-option *ngFor="let addLoiTaskGroup of AddLoiTaskGroups" [value]="addLoiTaskGroup">
          <div class="task-label">
            <mat-icon>{{ Tasks[addLoiTaskGroup].icon }}</mat-icon>
            <div>{{ Tasks[addLoiTaskGroup].label }}</div>
          </div>
        </mat-option>
      </mat-select>
    </div>

    <div *ngIf="!GeometryTasks.includes(taskGroup)" class="task-label">
      <mat-icon>{{ Tasks[taskGroup].icon }}</mat-icon>
      <div>{{ Tasks[taskGroup].label }}</div>
    </div>

    <div class="toolbar-right" *ngIf="taskGroup !== TaskGroup.INSTRUCTIONS">
      <div>
        <mat-label i18n="@@app.labels.required">Required</mat-label>

        <mat-slide-toggle formControlName="required" color="primary"></mat-slide-toggle>
      </div>

      <button mat-icon-button [matMenuTriggerFor]="menu" *ngIf="!addLoiTask">
        <mat-icon>more_vert</mat-icon>
      </button>

      <mat-menu #menu="matMenu">
        <button mat-menu-item (click)="onTaskDuplicate()">
          <span i18n="@@app.labels.duplicate">Duplicate</span>
        </button>

        <button mat-menu-item (click)="onTaskConditionToggle()">
          @if (!conditionControl) {
            <ng-container i18n="@@app.controls.tasks.addCondition"
              >Add condition</ng-container
            >
          } @else {
            <ng-container i18n="@@app.controls.tasks.removeCondition"
              >Remove condition</ng-container
            >
          }
        </button>

        <mat-divider></mat-divider>

        <button mat-menu-item (click)="onTaskDelete()">
          <ng-container i18n="@@app.labels.delete">Delete</ng-container>
        </button>
      </mat-menu>
    </div>

    <div class="toolbar-right" *ngIf="taskGroup === TaskGroup.INSTRUCTIONS">
      <button mat-icon-button [matMenuTriggerFor]="menu">
        <mat-icon>more_vert</mat-icon>
      </button>

      <mat-menu #menu="matMenu">
        <button mat-menu-item (click)="onTaskDelete()">
          <ng-container i18n="@@app.labels.delete">Delete</ng-container>
        </button>
      </mat-menu>
    </div>
  </div>

  <div class="task-form">
    <mat-form-field class="question" appearance="fill">
      <mat-label>{{ Tasks[taskGroup].placeholder }}</mat-label>

      <input
        #questionInput
        matInput
        formControlName="label"
        autocomplete="off"
      />

      <mat-error *ngIf="labelControl.touched && labelControl.invalid">
        <ng-container *ngIf="labelControl.getError('required')">
          {{ Tasks[taskGroup].requiredMessage }}
        </ng-container>
      </mat-error>
    </mat-form-field>

    <mat-form-field
      class="task-type"
      appearance="fill"
      *ngIf="taskGroup === TaskGroup.QUESTION"
      [matTooltip]="typeLocked() ? typeLockedMessage : ''"
    >
      <mat-select
        class="task-type-select"
        [disabled]="typeLocked()"
        [value]="taskTypeOption"
        (selectionChange)="onTaskTypeSelect($event.value)"
        (openedChange)="onTaskFocus()"
      >
        <mat-select-trigger>
          <mat-icon class="icon-task-type">{{ taskTypeOption.icon }}</mat-icon>
          <span>{{ taskTypeOption.label }}</span>
        </mat-select-trigger>

        <mat-option *ngFor="let taskTypeOption of TaskTypeOptions" [value]="taskTypeOption" class="icon-task-type-option">
          <mat-icon>{{ taskTypeOption.icon }}</mat-icon>
          <span>{{ taskTypeOption.label }}</span>
        </mat-option>
      </mat-select>
    </mat-form-field>
  </div>

  <div
    *ngIf="typeControl.value === TaskType.MULTIPLE_CHOICE"
    class="edit-options"
    cdkDropList
    (cdkDropListDropped)="drop($event)"
  >
    <ground-edit-option
      *ngFor="let formGroup of optionsControl.controls; let i = index;"
      [formGroup]="formGroup"
      [index]="i"
      [type]="cardinalityControl.value"
      (delete)="openDeleteOptionDialog(i)"
    >
    </ground-edit-option>

    <ground-edit-option
      *ngIf="hasOtherOptionControl?.value"
      [formGroup]="otherOption"
      [index]="-1"
      [type]="cardinalityControl.value"
      (delete)="openDeleteOptionDialog()"
    >
    </ground-edit-option>

    <div class="option-add-buttons">
      <mat-icon class="option-icon">
        {{ cardinalityControl.value === Cardinality.SELECT_ONE ? 'radio_button_unchecked' : 'check_box_outline_blank'}}
      </mat-icon>

      <button
        mat-button
        type="button"
        (click)="onAddOption()"
        class="add-option"
      >
        <ng-container i18n="@@app.taskEditor.multipleChoice.addOption"
          >Add option</ng-container
        >
      </button>

      <ng-container *ngIf="!hasOtherOptionControl.value">
        <div i18n="@@app.labels.or">or</div>

        <button
          mat-button
          type="button"
          (click)="onAddOtherOption()"
          color="primary"
          class="add-option"
        >
          <ng-container i18n="@@app.taskEditor.multipleChoice.addOther"
            >add "Other"</ng-container
          >
        </button>
      </ng-container>
    </div>
  </div>
</form>
